import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {without} from 'lodash-es';

import {createCompPasses} from 'api/comp-passes/create-comp-passes';
import {deleteCompPass} from 'api/comp-passes/delete-comp-pass';
import {getCompPasses} from 'api/comp-passes/get-comp-passes';
import {getCompPassActivity} from 'api/comp-passes/get-comp-pass-activity';
import {getCompPassSummaryCounts} from 'api/comp-passes/get-comp-pass-summary-counts';
import {
    type CompPassBatchResults,
    type CompPassRecipient,
    type CompPassesResults,
    type CompPassSummaryCount,
    type CreateCompPassResponse,
    type GetCompPassesParams,
} from 'api/comp-passes/comp-pass.types';
import {type UpcomingEvent} from 'api/events/events.types';
import {type CompPassConfirmationErrors} from 'pages/comp-pass/components/comp-pass-wizard/comp-pass-wizard.types';
import {initialState} from 'pages/comp-pass/store/comp-pass-slice';
import dayjs from 'utils/dayjs';

interface GetCompPassesDataParams {
    facilityId: number;
    requestData: GetCompPassesParams;
    handleAbortSignal?: (signal: AbortSignal) => void;
    abortSignal?: AbortSignal;
}
export interface ListingFiltersPayload {
    eventTypeIds?: number[];
    destinationIds?: number[];
    toDate: string;
    fromDate: string;
    email?: string;
    compPassBatchId?: string;
}
interface DeleteCompPassParams {
    facilityId: number;
    compPassId: number;
    handleAbortSignal?: (signal: AbortSignal) => void;
    abortSignal?: AbortSignal;
}

interface GetCompPassBatchesDataParams {
    facilityId: number;
    page: number;
    perPage: number;
    orderBy?: string;
    handleAbortSignal?: (signal: AbortSignal) => void;
    abortSignal?: AbortSignal;
}

interface GetCompPassSummaryCountParams {
    facilityId: number;
    date?: dayjs.Dayjs;
}

/**
 * Action to increment the creation step.
 */
export const incrementCreationStep = createAction(
    'compPass/incrementCreationStep'
);

/**
 * Action to decrement the creation step.
 */
export const decrementCreationStep = createAction(
    'compPass/decrementCreationStep'
);

/**
 * Action to set the current creation step.
 */
export const setCreationStep = createAction<number>('compPass/setCreationStep');

/**
 * Action to set a global error message.
 */
export const setGlobalError = createAction<string>('compPass/setGlobalError');

/**
 * Action to reset the comp pass creation state.
 */
export const resetCreationFlow = createAction('compPass/resetCreationFlow');

/**
 * Action to set the method for adding comp pass recipients.
 */
export const setCreationMethod = createAction<string>(
    'compPass/setCreationMethod'
);

/**
 * Action to set the active tab index for the main comp pass listing page.
 */
export const setActiveTabIndex = createAction<number>(
    'compPass/setActiveTabIndex'
);

/**
 * Fetches the comp passes paginated data for a given facility.
 *
 * This action creator makes an asynchronous call to the API to retrieve
 * the comp passes data for the specified facility.
 *
 * Action Type: 'compPasses/getCompPassesData'
 */
export const getCompPassesData = createAsyncThunk<
    CompPassesResults,
    GetCompPassesDataParams
>(
    'compPasses/getCompPassesData',
    async (
        {facilityId, requestData, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response: CompPassesResults = await getCompPasses(
                facilityId,
                requestData,
                {
                    signal: abortSignal,
                }
            );

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk to fetch comp pass creation history (batches).
 *
 * This action creator makes an asynchronous call to the API to retrieve
 * the comp passes creation history (batches) for the specified facility.
 *
 * Action Type: 'compPass/getPassCreationHistory'
 */
export const getPassCreationHistory = createAsyncThunk<
    CompPassBatchResults,
    GetCompPassBatchesDataParams
>(
    'compPass/getPassCreationHistory',
    async (
        {facilityId, page, perPage, orderBy, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getCompPassActivity({
                facilityId,
                params: {
                    page,
                    perPage,
                    orderBy,
                },
                config: {
                    signal: abortSignal,
                },
            });

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

interface ConfirmAndSendCompPassesArgs {
    facilityId: number;
    events: UpcomingEvent[];
    recipients: CompPassRecipient[];
}

/**
 * Thunk to confirm and send comp passes.
 */
export const confirmAndSendCompPasses = createAsyncThunk<
    CreateCompPassResponse,
    ConfirmAndSendCompPassesArgs,
    {rejectValue: CompPassConfirmationErrors}
>(
    'compPass/confirmAndSendCompPasses',
    async ({facilityId, events, recipients}, {rejectWithValue}) => {
        try {
            const response = await createCompPasses({
                facilityId,
                requestParams: {
                    events,
                    recipients,
                },
            });

            return response;
        } catch (error) {
            const errorResponse =
                error.status >= 400 && error.status < 500
                    ? error.response.data.data.errors
                    : error.message;

            return rejectWithValue(errorResponse);
        }
    }
);

/**
 * Deletes a comp pass for a given facility.
 *
 * This action creator makes an asynchronous call to the API to delete
 * a comp pass for the specified facility.
 *
 * Action Type: 'compPasses/deleteCompPass'
 */
export const deleteCompPassData = createAsyncThunk<
    number,
    DeleteCompPassParams
>(
    'compPasses/deleteCompPass',
    async (
        {facilityId, compPassId, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            await deleteCompPass(facilityId, compPassId, {
                signal: abortSignal,
            });

            return compPassId;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Action to update filters with the count of applied filters
 *
 * Action Type: compPass/updateFilters
 */
export const updateListingFilters = createAction(
    'compPass/updateFilters',
    (payload: ListingFiltersPayload) => {
        const appliedFilters = without(
            Object.values(payload),
            ...Object.values(initialState.passListing.filters)
        );

        let appliedFiltersCount = 0;

        appliedFilters.forEach((filter) => {
            if (filter.length > 0) {
                appliedFiltersCount++;
            }
        });

        return {
            payload: {
                ...payload,
                ...(payload.compPassBatchId && {
                    compPassBatchId: Number(payload.compPassBatchId),
                }),
                numFiltersApplied: appliedFiltersCount,
            },
        };
    }
);

/**
 * Thunk to get the comp pass summary counts data
 *
 * Action Type: 'compPass/getSummaryCounts'
 */
export const getCompPassSummaryCountData = createAsyncThunk<
    CompPassSummaryCount,
    GetCompPassSummaryCountParams
>(
    'compPass/getSummaryCounts',
    async ({facilityId, date}, {rejectWithValue}) => {
        try {
            const response = await getCompPassSummaryCounts({
                facilityId,
                ...(date && {date}),
            });

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);
